// /Users/stuartmcgeer/portalworx/frontend/src/contexts/WebSocketContext.tsx

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

interface WebSocketContextProps {
  sendMessage: (data: any) => void;
  lastMessage: any | null;
}

const WebSocketContext = createContext<WebSocketContextProps | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, username } = useAuth();
  const [lastMessage, setLastMessage] = useState<any | null>(null);
  const socket = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !username) return;

    // Fetch the token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('WebSocket connection failed: Missing authentication token.');
      return;
    }

    // Determine the protocol dynamically based on the current page protocol
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsBaseUrl = process.env.REACT_APP_WS_BASE_URL || window.location.host;
    const wsUrl = `${wsProtocol}//${wsBaseUrl}/ws/direct-messages/?token=${encodeURIComponent(token)}`;

    // Create the WebSocket connection
    const connectWebSocket = () => {
      socket.current = new WebSocket(wsUrl);
    
      socket.current.onmessage = (event) => {
        try {
          setLastMessage(JSON.parse(event.data));
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };
    
      socket.current.onopen = () => {
        console.log('WebSocket connected');
      };
    
      socket.current.onclose = (event) => {
        console.log('WebSocket disconnected. Reconnecting in 5 seconds...', event.reason);
        setTimeout(connectWebSocket, 5000); // Attempt reconnection after 5 seconds
      };
    
      socket.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    };
    
    connectWebSocket();    

    // Cleanup on component unmount or dependency changes
    return () => {
      if (socket.current) {
        socket.current.close();
      }
    };
  }, [isAuthenticated, username]);

  const sendMessage = (data: any) => {
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify(data));
    } else {
      console.error('WebSocket is not open. Cannot send message.');
    }
  };

  return (
    <WebSocketContext.Provider value={{ sendMessage, lastMessage }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextProps => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
